interface ServiceDetail {
  id: string;
  icon: string;
  title: string;
  shortDescription: string;
  fullDescription: string;
  process: string[];
  materials: string[];
  precautions: string[];
  limits: string[];
  duration: string;
  price: string;
  guarantee: string;
  /** True for add-ons that attach to a base service (Standard, Luxe, Enfant). */
  isOption?: boolean;
}

interface ServiceDetails {
  [key: string]: ServiceDetail;
}

interface LocalizedServiceDetails {
  fr: ServiceDetails;
  en: ServiceDetails;
}

const frenchServiceDetails: ServiceDetails = {
  "nettoyage-professionnel": {
    id: "nettoyage-professionnel",
    icon: "🧽",
    title: "Nettoyage Professionnel",
    shortDescription:
      "Nettoyage en profondeur, respectueux des matières, avec produits et techniques professionnelles.",
    fullDescription:
      "Notre service de nettoyage professionnel redonne à vos chaussures leur éclat d'origine tout en respectant les matières (cuir, daim/nubuck, toile, techniques). Chaque paire est analysée individuellement: nous choisissons des produits adaptés, des brosses et techniques douces, et un séchage contrôlé pour éviter toute déformation. Idéal pour un entretien complet, un décrassage après intempéries, ou une remise à neuf avant un événement.",
    process: [
      "Diagnostic détaillé (matière, état, fragilités, tests couleur)",
      "Dépoussiérage et dégraissage ciblé",
      "Application de produits adaptés à la matière (pH, solvant)",
      "Nettoyage manuel en profondeur avec brosses dédiées",
      "Séchage contrôlé (formes, circulation d’air, loin des sources de chaleur)",
      "Finitions (réhydratation du cuir, correction teinte légère si nécessaire)",
      "Contrôle qualité final et conseils d’entretien",
    ],
    materials: [
      "Chaussures de sport/ville",
      "Cuir lisse et grainé",
      "Daim et nubuck",
      "Toile et textile",
      "Matériaux synthétiques",
      "Cuir verni",
      "Matériaux techniques (Gore-Tex, mesh technique, etc.)",
    ],
    precautions: [
      "Certaines taches anciennes (peinture, huile) peuvent rester atténuées mais visibles",
      "Risque de décoloration sur matières instables ou teintures maison",
      "Les cuirs très secs peuvent nécessiter une nutrition complémentaire",
      "Le séchage peut prendre jusqu’à 48h selon la matière et la météo",
    ],
    limits: [
      "Ne remplace pas une réparation (déchirures, semelles décollées majeures)",
      "Peintures tenaces, colles oxydées et jaunissement profond non garantis",
      "Matières exotiques (python, autruche, etc.) sur évaluation uniquement",
      "Résultat dépendant de l’état initial et de la qualité de la matière",
    ],
    duration: "2-7 jours (incluant séchage et finitions)",
    price: "À partir de 20€",
    guarantee: "",
    // "Satisfaction garantie: ajustements/finitions offerts sous 7 jours (dans la limite des contraintes matière)",
  },

  "collecte-livraison": {
    id: "collecte-livraison",
    icon: "🚚",
    title: "Collecte & Livraison",
    isOption: true,
    shortDescription:
      "Option à ajouter à votre service de base : on vient chercher vos paires chez vous et on vous les ramène, à partir de 15€.",
    fullDescription:
      "La collecte & livraison est une option que vous activez au moment de la commande, après avoir choisi votre service de base (Standard, Luxe ou Enfant). Nous récupérons vos chaussures chez vous, les traitons en atelier, puis vous les rapportons à l’horaire convenu. Chaque paire est étiquetée et suivie, avec emballage soigné et communication transparente sur l’avancement. Sans cette option, vous déposez et récupérez vos paires en point relais partenaire.",
    process: [
      "Réservation du créneau (en ligne ou par téléphone)",
      "Collecte à domicile: vérification de l’état et inventaire",
      "Étiquetage individuel et emballage de protection",
      "Transport sécurisé vers l’atelier",
      "Traitement selon le service choisi (nettoyage, etc.)",
      "Notification de disponibilité et prise de RDV de livraison",
      "Livraison retour et contrôle avec vous",
    ],
    materials: [
      "Tous types de chaussures acceptés",
      "Emballage sécurisé fourni",
      "Étiquetage individuel de chaque paire",
      "Protection anti-choc et anti-humidité",
    ],
    precautions: [
      "Présence requise ou personne mandatée lors de la collecte et de la livraison",
      "Merci de retirer objets personnels des chaussures (semelles, capteurs) si besoin",
      "Créneaux susceptibles d’évoluer en cas d’aléas de circulation",
      "Service disponible du lundi au dimanche (jours fériés inclus)",
    ],
    limits: [
      "Zone couverte: ville + proche banlieue (au‑delà: devis ou indisponible)",
      "Objets de grande valeur: signalement requis (assurance limitée)",
      "Impossibilité de livraison en l’absence non signalée (frais de re‑présentation)",
    ],
    duration: "Créneaux le jour même ou J+1 selon disponibilité",
    price: "Option · à partir de 15€ par commande",
    guarantee:
      "Créneau respecté ou re‑programmation prioritaire + geste commercial",
  },

  "service-express": {
    id: "service-express",
    icon: "⚡",
    title: "Service Express",
    isOption: true,
    shortDescription:
      "Option à ajouter à votre service de base : traitement prioritaire et restitution en 24h ou 48h, au choix.",
    fullDescription:
      "L’express est une option que vous activez au moment de la commande, sur le service de base de votre choix (Standard, Luxe ou Enfant). Deux délais : 24h ou 48h. Votre commande passe en priorité à l’atelier et nous adaptons les techniques pour accélérer le séchage tout en préservant la matière. Idéal avant un événement, un voyage ou une urgence professionnelle.",
    process: [
      "Validation d'éligibilité (matière/état) et devis express",
      "Prise en charge prioritaire et diagnostic immédiat",
      "Nettoyage intensif avec protocoles optimisés",
      "Séchage accéléré en environnement contrôlé",
      "Finitions essentielles et contrôle qualité",
      "Remise en main propre ou livraison sous 24h ou 48h selon l’option choisie",
    ],
    materials: [
      "Chaussures de sport/ville",
      "Cuir lisse & synthétique compatibles",
      "Toile résistante et mesh techniques",
      "Daim, nubuck, cuirs très délicats",
    ],
    precautions: [
      "Disponibilité variable selon volume et créneaux",
      "Séchage accéléré déconseillé pour matières fragiles",
      "Certaines taches anciennes ne peuvent être totalement retirées",
      "Peut nécessiter un entretien complémentaire ultérieur (nutrition du cuir)",
    ],
    limits: [
      "Exclusion: daim/nubuck très sensibles, cuirs non stabilisés",
      "Périodes de forte demande: créneaux limités",
      "Supplément express dû en cas d'annulation tardive (<12h)",
    ],
    duration:
      "24h ou 48h selon l’option choisie (prise en charge à restitution)",
    price: "Option · supplément selon le délai (24h ou 48h)",
    guarantee: "Si le délai n'est pas tenu, supplément express remboursé",
  },

  "protection-premium": {
    id: "protection-premium",
    icon: "🛡️",
    title: "Protection Premium",
    isOption: true,
    shortDescription:
      "Option à ajouter à votre service de base : barrière déperlante durable, à partir de 2€ par paire.",
    fullDescription:
      "La Protection Premium est une option que vous activez au moment de la commande, en complément de votre service de base (Standard, Luxe ou Enfant). Après le nettoyage complet, nous appliquons un protecteur adapté à la matière (cuir, toile, daim) qui dépose une barrière invisible contre l'eau et les taches du quotidien, sans étouffer la matière, puis laissons polymériser pour une efficacité optimale. Idéal après un achat neuf ou un grand nettoyage.",
    process: [
      "Nettoyage et préparation minutieuse des surfaces",
      "Séchage complet et contrôle d'humidité",
      "Application uniforme du protecteur selon la matière",
      "Polymérisation/prise du traitement (temps contrôlé)",
      "Test de perlage et contrôle homogénéité",
      "Conseils d’entretien et renouvellement",
    ],
    materials: [
      "Cuir lisse et grainé",
      "Daim et nubuck (formules spécifiques)",
      "Toile et textiles",
      "Synthétiques",
      "Membranes techniques respirantes (type Gore‑Tex)",
    ],
    precautions: [
      "Efficacité variable selon l’usage et la matière",
      "Peut légèrement foncer certains suèdes/nubucks",
      "Test préalable en zone discrète recommandé",
    ],
    limits: [
      "Protection non permanente (entretien requis)",
      "Ne corrige pas les usures mécaniques ou craquelures",
      "Matériaux très abîmés: efficacité limitée",
      "Matières exotiques: au cas par cas",
    ],
    duration:
      "S’ajoute au délai du service de base (temps de polymérisation inclus)",
    price: "Option · à partir de 2€ par paire",
    guarantee: "Efficacité jusqu’à 30 jours (selon usage et conditions)",
  },

  // "reparation-mineure": {
  //   id: "reparation-mineure",
  //   icon: "🔧",
  //   title: "Réparation Mineure",
  //   shortDescription:
  //     "Petites réparations soignées pour prolonger la vie de vos chaussures.",
  //   fullDescription:
  //     "Nous traitons les défauts courants (coutures, œillets, petits décollements) avec des matériaux de qualité et des techniques propres à chaque matière. L’objectif: prolonger la durée de vie et la sécurité d’usage, tout en conservant l’esthétique.",
  //   process: [
  //     "Diagnostic et devis rapide (photos possibles)",
  //     "Préparation des zones (dégraissage/ponçage léger)",
  //     "Réparation ciblée (colle, couture, remplacement pièce)",
  //     "Séchage/prise et test de solidité",
  //     "Nettoyage de finition",
  //     "Contrôle qualité et conseils d’entretien",
  //   ],
  //   materials: [
  //     "Coutures et surpiqûres",
  //     "Œillets et passants",
  //     "Petites déchirures superficielles",
  //     "Décollements de semelle mineurs",
  //     "Remplacements d'accessoires (lacets, etc.)",
  //   ],
  //   precautions: [
  //     "Évaluation nécessaire pour confirmer la faisabilité",
  //     "Légère visibilité possible selon la zone/matière",
  //     "Délais variables selon la complexité",
  //     "Matériaux équivalents si pièces d’origine indisponibles",
  //   ],
  //   limits: [
  //     "Changements de semelles complets et réparations majeures exclus",
  //     "Semelles totalement décollées non prises en charge",
  //     "Grandes déchirures structurelles non réparable",
  //     "Chaussures de sécurité non prises en charge",
  //   ],
  //   duration: "2–7 jours selon tarif et complexité",
  //   price:
  //     "Inclus tarif Premium (réparations mineures) et sur devis (réparations majeures)",
  //   guarantee: "Solidité 6 mois sur la zone réparée (usage normal)",
  // },

  "finition-luxe": {
    id: "finition-luxe",
    icon: "✨",
    title: "Finition Luxe",
    shortDescription:
      "Cirage premium, polissage miroir et finitions haute couture.",
    fullDescription:
      "La Finition Luxe sublime vos chaussures: nutrition profonde, cirage haut de gamme, patine légère si souhaitée, et polissage miroir. Chaque détail (lacets, œillets) est soigné, puis vos chaussures sont présentées dans un emballage raffiné.",
    process: [
      "Nettoyage et préparation minutieuse",
      "Nutrition au lait/crème de haute qualité",
      "Cirage premium (couches fines superposées)",
      "Polissage manuel effet miroir",
      "Finitions détaillées (lacets, œillets, chiffonnage)",
      "Emballage luxe (papier de soie/boîte)",
    ],
    materials: [
      "Cuir lisse premium",
      "Cuir grainé de qualité",
      "Cuir verni haut de gamme",
      "Matériaux nobles exclusivement",
      "Chaussures de luxe et prestige",
    ],
    precautions: ["Produits premium exclusivement"],
    limits: [
      "Corrections profondes de couleur non incluses",
      "Service sur devis pour demandes spécifiques (patine)",
    ],
    duration: "2–7 jours selon tarif et complexité",
    price: "Inclus tarif Premium",
    guarantee: "Satisfaction garantie",
  },
};

const englishServiceDetails: ServiceDetails = {
  "nettoyage-professionnel": {
    id: "nettoyage-professionnel",
    icon: "🧽",
    title: "Professional Cleaning",
    shortDescription:
      "Deep cleaning, respectful of materials, with professional products and techniques.",
    fullDescription:
      "Our professional cleaning service restores your shoes to their original shine while respecting materials (leather, suede/nubuck, canvas, technical). Each pair is analyzed individually: we choose suitable products, gentle brushes and techniques, and controlled drying to avoid any deformation. Ideal for complete maintenance, cleaning after bad weather, or refreshing before an event.",
    process: [
      "Detailed diagnosis (material, condition, fragilities, color tests)",
      "Targeted dusting and degreasing",
      "Application of material-appropriate products (pH, solvent)",
      "Deep manual cleaning with dedicated brushes",
      "Controlled drying (forms, air circulation, away from heat sources)",
      "Finishing touches (leather rehydration, light color correction if needed)",
      "Final quality control and maintenance advice",
    ],
    materials: [
      "Sport/dress shoes",
      "Smooth and grained leather",
      "Suede and nubuck",
      "Canvas and textile",
      "Synthetic materials",
      "Patent leather",
      "Technical materials (Gore-Tex, technical mesh, etc.)",
    ],
    precautions: [
      "Some old stains (paint, oil) may remain attenuated but visible",
      "Very dry leathers may require additional nutrition",
      "Drying may take up to 48h depending on material and weather",
    ],
    limits: [
      "Does not replace repair (tears, major sole detachment)",
      "Exotic materials (python, ostrich, etc.) by evaluation only",
      "Result depends on initial condition and material quality",
    ],
    duration: "2-7 days (including drying and finishing)",
    price: "From €20",
    guarantee: "",
  },

  "collecte-livraison": {
    id: "collecte-livraison",
    icon: "🚚",
    title: "Pickup & Delivery",
    isOption: true,
    shortDescription:
      "Add-on for your base service: we collect your pairs at home and bring them back, from €15.",
    fullDescription:
      "Pickup & delivery is an add-on you enable when ordering, after choosing your base service (Standard, Luxury or Kids). We collect your shoes at your home, treat them in our workshop, then bring them back at the agreed time. Each pair is labeled and tracked, with careful packaging and transparent communication on progress. Without this add-on, you drop off and collect your pairs at a partner relay point.",
    process: [
      "Slot booking (online or by phone)",
      "Home pickup: condition check and inventory",
      "Individual labeling and protective packaging",
      "Secure transport to workshop",
      "Treatment according to chosen service (cleaning, etc.)",
      "Availability notification and delivery appointment",
      "Return delivery and check with you",
    ],
    materials: [
      "All types of shoes accepted",
      "Secure packaging provided",
      "Individual labeling of each pair",
      "Anti-shock and anti-humidity protection",
    ],
    precautions: [
      "Presence required or authorized person during pickup and delivery",
      "Please remove personal items from shoes (insoles, sensors) if needed",
      "Slots may change due to traffic conditions",
      "Service available Monday to Sunday (holidays included)",
    ],
    limits: [
      "Coverage area: city + close suburbs (beyond: quote or unavailable)",
      "High-value items: reporting required (limited insurance)",
      "Delivery impossible in unreported absence (re-presentation fee)",
    ],
    duration: "Same day or next day slots depending on availability",
    price: "Add-on · from €15 per order",
    guarantee: "Slot respected or priority rescheduling + commercial gesture",
  },

  "service-express": {
    id: "service-express",
    icon: "⚡",
    title: "Express Service",
    isOption: true,
    shortDescription:
      "Add-on for your base service: priority handling and return within 24h or 48h, your choice.",
    fullDescription:
      "Express is an add-on you enable when ordering, on the base service of your choice (Standard, Luxury or Kids). Two turnarounds: 24h or 48h. Your order gets workshop priority and we adapt techniques to accelerate drying while preserving the material. Ideal before an event, trip, or professional emergency.",
    process: [
      "Eligibility validation (material/condition) and express quote",
      "Priority handling and immediate diagnosis",
      "Intensive cleaning with optimized protocols",
      "Accelerated drying in controlled environment",
      "Essential finishing and quality control",
      "Hand delivery or delivery within 24h or 48h depending on the chosen option",
    ],
    materials: [
      "Sport/dress shoes",
      "Compatible smooth leather & synthetic",
      "Resistant canvas and technical mesh",
      "Suede, nubuck, very delicate leathers",
    ],
    precautions: [
      "Availability varies according to volume and slots",
      "Accelerated drying not recommended for fragile materials",
      "Some old stains cannot be completely removed",
      "May require additional maintenance later (leather nutrition)",
    ],
    limits: [
      "Exclusion: very sensitive suede/nubuck, unstabilized leathers",
      "High demand periods: limited slots",
      "Express supplement due for late cancellation (<12h)",
    ],
    duration: "24h or 48h depending on the chosen option (handling to return)",
    price: "Add-on · supplement depends on turnaround (24h or 48h)",
    guarantee: "If deadline not met, express supplement refunded",
  },

  "protection-premium": {
    id: "protection-premium",
    icon: "🛡️",
    title: "Premium Protection",
    isOption: true,
    shortDescription:
      "Add-on for your base service: a durable water-repellent barrier, from €2 per pair.",
    fullDescription:
      "Premium Protection is an add-on you enable when ordering, on top of your base service (Standard, Luxury or Kids). After the complete cleaning, we apply a protector suited to the material (leather, canvas, suede) that deposits an invisible barrier against water and daily stains without suffocating the material, then let it polymerize for optimal effectiveness. Ideal after a new purchase or deep cleaning.",
    process: [
      "Cleaning and meticulous surface preparation",
      "Complete drying and humidity control",
      "Uniform application of protector according to material",
      "Polymerization/curing of treatment (controlled time)",
      "Beading test and homogeneity control",
      "Maintenance and renewal advice",
    ],
    materials: [
      "Smooth and grained leather",
      "Suede and nubuck (specific formulas)",
      "Canvas and textiles",
      "Synthetics",
      "Breathable technical membranes (Gore-Tex type)",
    ],
    precautions: [
      "Effectiveness varies according to use and material",
      "May slightly darken some suedes/nubucks",
      "Preliminary test in discrete area recommended",
    ],
    limits: [
      "Non-permanent protection (maintenance required)",
      "Does not correct mechanical wear or cracks",
      "Very damaged materials: limited effectiveness",
      "Exotic materials: case by case",
    ],
    duration:
      "Adds to the base service turnaround (polymerization time included)",
    price: "Add-on · from €2 per pair",
    guarantee: "Effectiveness up to 30 days (depending on use and conditions)",
  },

  // "reparation-mineure": {
  //   id: "reparation-mineure",
  //   icon: "🔧",
  //   title: "Minor Repair",
  //   shortDescription:
  //     "Careful small repairs to extend the life of your shoes.",
  //   fullDescription:
  //     "We treat common defects (stitching, eyelets, small detachments) with quality materials and techniques specific to each material. The goal: extend lifespan and usage safety, while maintaining aesthetics.",
  //   process: [
  //     "Diagnosis and quick quote (photos possible)",
  //     "Area preparation (degreasing/light sanding)",
  //     "Targeted repair (glue, stitching, part replacement)",
  //     "Drying/curing and strength test",
  //     "Finishing cleaning",
  //     "Quality control and maintenance advice",
  //   ],
  //   materials: [
  //     "Stitching and topstitching",
  //     "Eyelets and loops",
  //     "Small superficial tears",
  //     "Minor sole detachments",
  //     "Accessory replacements (laces, etc.)",
  //   ],
  //   precautions: [
  //     "Evaluation necessary to confirm feasibility",
  //     "Slight visibility possible depending on area/material",
  //     "Variable delays according to complexity",
  //     "Equivalent materials if original parts unavailable",
  //   ],
  //   limits: [
  //     "Complete sole changes and major repairs excluded",
  //     "Completely detached soles not handled",
  //     "Large structural tears not repairable",
  //     "Safety shoes not handled",
  //   ],
  //   duration: "2–7 days depending on rate and complexity",
  //   price:
  //     "Included in Premium rate (minor repairs) and by quote (major repairs)",
  //   guarantee: "6-month strength on repaired area (normal use)",
  // },

  "finition-luxe": {
    id: "finition-luxe",
    icon: "✨",
    title: "Luxury Finish",
    shortDescription:
      "Premium waxing, mirror polishing and haute couture finishes.",
    fullDescription:
      "Luxury Finish enhances your shoes: deep nutrition, high-end waxing, light patina if desired, and mirror polishing. Every detail (laces, eyelets) is cared for, then your shoes are presented in refined packaging.",
    process: [
      "Cleaning and meticulous preparation",
      "High-quality milk/cream nutrition",
      "Premium waxing (superimposed thin layers)",
      "Manual mirror-effect polishing",
      "Detailed finishing (laces, eyelets, buffing)",
      "Luxury packaging (tissue paper/box)",
    ],
    materials: [
      "Premium smooth leather",
      "Quality grained leather",
      "High-end patent leather",
      "Noble materials exclusively",
      "Luxury and prestige shoes",
    ],
    precautions: ["Premium products exclusively"],
    limits: [
      "Deep color corrections not included",
      "Service by quote for specific requests (patina)",
    ],
    duration: "2–7 days depending on rate and complexity",
    price: "Included in Premium rate",
    guarantee: "Satisfaction guaranteed",
  },
};

export const localizedServiceDetails: LocalizedServiceDetails = {
  fr: frenchServiceDetails,
  en: englishServiceDetails,
};

// For backward compatibility
export const serviceDetails = frenchServiceDetails;
